// The folded rail at a phone's width (02-DESIGN/03-console/01-the-console.md
// § the look: at a phone's width; hq issue 220): under 900 px the rail folds
// behind the bar's *Menu* button, and pressed, the rail's three regions open
// in place of the page. Whether the rail is open lives in the history entry's
// state, as a form does (§ conventions: a form; ../ui/form.tsx): opening
// pushes an entry on the same address, so Back closes it; the button closes
// it by replacing the entry; a pick replaces the open entry with the page
// picked, so Back from there returns to the page the rail was opened over.

/** KEY is the one field of a history entry's state that says the rail is open. */
const KEY = "rail";

/** NARROW is the width under which the rail folds — the same 900 px the styles fold it at. */
export const NARROW = "(max-width: 900px)";

function record(state: unknown): Record<string, unknown> | undefined {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : undefined;
}

/** railOpen reads whether a history entry's state has the rail open. */
export function railOpen(state: unknown): boolean {
  return record(state)?.[KEY] === true;
}

/** withRail is the entry's state with the rail open and the rest of it kept. */
export function withRail(state: unknown): Record<string, unknown> {
  return { ...(record(state) ?? {}), [KEY]: true };
}

/** withoutRail is the entry's state with the rail closed: the rest of it, or null where nothing else was in it. */
export function withoutRail(state: unknown): Record<string, unknown> | null {
  const r = record(state);
  if (r === undefined) {
    return null;
  }
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (k !== KEY) {
      rest[k] = v;
    }
  }
  return Object.keys(rest).length === 0 ? null : rest;
}

/**
 * foldedAt is whether the rail stands open in place of the page: only where
 * the entry has it open and the window is under 900 px. A `rail` left in the
 * entry at a wide width — a resize, a rotation, a refresh — opens nothing, so
 * the rail's links push as they always do and nothing listens for Escape.
 */
export function foldedAt(open: boolean, narrow: boolean): boolean {
  return open && narrow;
}

/**
 * leaveThen is a move out of the open rail through the workspace menu: the
 * rail closes first, replacing its open entry, and then the move is made, so
 * Back from the workspace it goes to returns to the page underneath, closed.
 */
export function leaveThen(leave: (() => void) | undefined, move: () => void): void {
  leave?.();
  move();
}
